import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthProvider";
import {
  listAdmins,
  lookupUserByEmail,
  setAdminClaim,
  type AdminUser,
} from "../api/functions";

export function AdminsPage() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await listAdmins();
      setAdmins(r.admins);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Failed to load admins");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!admins) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-800">Admins</h1>
        <p className="text-slate-500 text-sm">
          Users with full access to the admin portal. Promote a customer or any
          existing Firebase Auth user by email.
        </p>
      </header>

      <AddAdminForm onAdded={refresh} />

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">UID</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium">Last sign-in</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {admins.map(a => (
              <AdminRow
                key={a.uid}
                admin={a}
                isSelf={a.uid === user?.uid}
                onChange={refresh}
              />
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        Newly promoted users must sign out and back in for the admin role to take effect.
      </p>
    </div>
  );
}

function AddAdminForm({ onAdded }: { onAdded: () => void }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const lookup = await lookupUserByEmail({ email });
      if (lookup.isAdmin) {
        setError(`${lookup.email} is already an admin.`);
        setBusy(false);
        return;
      }
      if (!confirm(`Grant admin access to ${lookup.email}?\n\nThey will be able to manage all accounts and licenses.`)) {
        setBusy(false);
        return;
      }
      await setAdminClaim({ uid: lookup.uid, isAdmin: true });
      setEmail("");
      onAdded();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Failed to add admin");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="bg-white rounded-lg shadow p-5 flex items-end gap-3">
      <label className="block flex-1">
        <span className="text-sm text-slate-700">Email of existing Firebase user</span>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          placeholder="user@example.com"
          className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 text-sm"
        />
      </label>
      <button
        type="submit"
        disabled={busy}
        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium rounded-md px-5 py-2 text-sm"
      >
        {busy ? "Adding…" : "+ Add admin"}
      </button>
      {error && <div className="text-sm text-red-600 self-center">{error}</div>}
    </form>
  );
}

function AdminRow({
  admin, isSelf, onChange,
}: { admin: AdminUser; isSelf: boolean; onChange: () => void }) {
  const [busy, setBusy] = useState(false);

  const onRevoke = async () => {
    if (!confirm(`Revoke admin access from ${admin.email}?\n\nThey'll lose access on their next sign-in.`)) return;
    setBusy(true);
    try {
      await setAdminClaim({ uid: admin.uid, isAdmin: false });
      onChange();
    } catch (err) {
      alert((err as { message?: string }).message ?? "Failed to revoke");
      setBusy(false);
    }
  };

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3 text-slate-800">
        {admin.email || <span className="text-slate-400">(no email)</span>}
        {isSelf && <span className="ml-2 text-xs text-indigo-600">(you)</span>}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-500">{admin.uid}</td>
      <td className="px-4 py-3 text-slate-600 text-xs">{formatDate(admin.createdAt)}</td>
      <td className="px-4 py-3 text-slate-600 text-xs">{formatDate(admin.lastSignIn) || "—"}</td>
      <td className="px-4 py-3 text-right">
        {isSelf ? (
          <span className="text-xs text-slate-400">cannot revoke self</span>
        ) : (
          <button
            disabled={busy}
            onClick={onRevoke}
            className="text-xs text-red-600 hover:underline disabled:opacity-50"
          >
            Revoke
          </button>
        )}
      </td>
    </tr>
  );
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString();
}
